import React, { useState, useEffect } from "react";
import { C, R, SH, F } from "../theme";
import { Icon } from "../icons";
import { Btn, IconBtn, Modal } from "./UI";

function InstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showGuide, setShowGuide] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem("pwa-install-dismissed") === "1"; } catch { return false; }
  });
  const [isInstalled, setIsInstalled] = useState(false);
  const [waited, setWaited] = useState(false);

  useEffect(() => {
    if (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone) {
      setIsInstalled(true);
      return;
    }
    const handler = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  // Wait 3s before showing manual guide (give time for beforeinstallprompt)
  useEffect(() => { const t = setTimeout(() => setWaited(true), 3000); return () => clearTimeout(t); }, []);

  if (isInstalled || dismissed) return null;

  // Detect browser type for manual instructions
  const ua = navigator.userAgent || "";
  const isIOS = /iPad|iPhone|iPod/.test(ua);
  const isSamsung = /SamsungBrowser/.test(ua);
  const isFirefox = /Firefox/.test(ua) && !/Seamonkey/.test(ua);
  const isOpera = /OPR|Opera/.test(ua);
  const isChrome = /Chrome/.test(ua) && !isSamsung && !isOpera;

  // Don't show anything until we've waited for the prompt event
  if (!deferredPrompt && !waited) return null;

  async function install() {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") setDeferredPrompt(null);
    } else {
      setShowGuide(true);
    }
  }

  function dismiss() {
    setDismissed(true);
    setShowGuide(false);
    try { sessionStorage.setItem("pwa-install-dismissed", "1"); } catch {}
  }

  const getInstructions = () => {
    if (isIOS) return [
      "Appuyez sur le bouton de partage, en bas de Safari",
      "Faites défiler et appuyez sur « Sur l'écran d'accueil »",
      "Confirmez en appuyant sur « Ajouter »"
    ];
    if (isSamsung) return [
      "Appuyez sur le menu, en bas à droite",
      "Appuyez sur « Ajouter la page à » puis « Écran d'accueil »",
      "Confirmez en appuyant sur « Ajouter »"
    ];
    if (isFirefox) return [
      "Appuyez sur le menu, en haut à droite",
      "Appuyez sur « Installer »",
      "Si absent, appuyez sur « Ajouter à l'écran d'accueil »"
    ];
    if (isOpera) return [
      "Appuyez sur le menu, en bas à droite",
      "Appuyez sur « Écran d'accueil »",
      "Confirmez en appuyant sur « Ajouter »"
    ];
    // Chrome / default
    return [
      "Appuyez sur le menu, en haut à droite",
      "Appuyez sur « Installer l'application » ou « Ajouter à l'écran d'accueil »",
      "Confirmez en appuyant sur « Installer »"
    ];
  };

  const browserName = isIOS ? "Safari" : isSamsung ? "Samsung Internet" : isFirefox ? "Firefox" : isOpera ? "Opera" : isChrome ? "Chrome" : "votre navigateur";

  return (
    <>
      <div
        className="animate-fadeInUp"
        style={{
          position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 9999,
          background: C.surface,
          borderTop: `1px solid ${C.border}`,
          boxShadow: SH.md,
          padding: "11px 14px",
          display: "flex", alignItems: "center", gap: 11,
          paddingBottom: "max(11px, env(safe-area-inset-bottom))",
        }}
      >
        <span style={{
          width: 32, height: 32, borderRadius: R.md, flexShrink: 0,
          background: C.navySoft, color: C.navy,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <Icon name="mobile" size={17} />
        </span>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="truncate" style={{ fontSize: F.size.md, fontWeight: F.weight.semi, color: C.ink }}>
            Installer Setal Express
          </div>
          <div className="truncate" style={{ fontSize: F.size.sm, color: C.inkMuted }}>
            Accès rapide depuis votre écran d'accueil
          </div>
        </div>

        <Btn small icon="download" onClick={install}>Installer</Btn>
        <IconBtn icon="close" title="Masquer" onClick={dismiss} />
      </div>

      {/* Guide d'installation manuel */}
      {showGuide && (
        <Modal
          title="Installer Setal Express"
          icon="mobile"
          width={420}
          onClose={() => setShowGuide(false)}
          footer={
            <>
              <Btn variant="white" onClick={dismiss}>Plus tard</Btn>
              <Btn icon="check" onClick={() => setShowGuide(false)}>J'ai compris</Btn>
            </>
          }
        >
          <p style={{ fontSize: F.size.base, color: C.inkMuted, marginBottom: 12 }}>
            Depuis {browserName} :
          </p>
          <ol style={{ listStyle: "none", display: "grid", gap: 8 }}>
            {getInstructions().map((step, i) => (
              <li
                key={i}
                style={{
                  display: "flex", alignItems: "flex-start", gap: 10,
                  background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: R.md,
                  padding: "10px 12px",
                }}
              >
                <span style={{
                  width: 20, height: 20, borderRadius: "50%", flexShrink: 0,
                  background: C.navy, color: C.inkOn,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: F.size.xs, fontWeight: F.weight.semi,
                }}>
                  {i + 1}
                </span>
                <span style={{ fontSize: F.size.base, color: C.inkBody, lineHeight: 1.55 }}>{step}</span>
              </li>
            ))}
          </ol>
        </Modal>
      )}
    </>
  );
}

export default InstallBanner;
